import { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, RotateCcw, Sparkles } from "lucide-react";

import { useTranslation } from 'react-i18next';
const TOUR_STEPS = [
  {
    target: "body",
    title: "Welcome to Max OS Cloud",
    content:
      "Let's get your first agent running. This quick tour will walk you through the key features of your dashboard so you can start automating in minutes.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: "[data-tour='stats-row']",
    title: "Dashboard Overview",
    content:
      "This is your control center. Here you can see your current plan, active agents, credit usage, and system status at a glance.",
    placement: "bottom",
  },
  {
    target: "[data-tour='agents-section']",
    title: "Agent Cards",
    content:
      "Each agent card shows the name, role, and live status of your AI worker. Green means active and ready to handle tasks. You can add more agents as your needs grow.",
    placement: "top",
  },
  {
    target: "[data-tour='channels-section']",
    title: "Channels",
    content:
      "Connect your agents to messaging platforms like Telegram, WhatsApp, Email, Discord, and Slack. Connected channels show a green 'Live' badge.",
    placement: "top",
  },
  {
    target: "[data-tour='storage-section']",
    title: "Knowledge Base & Storage",
    content:
      "Upload documents, training data, and memory files here. Your agents use this knowledge base to provide accurate, context-aware responses.",
    placement: "top",
  },
  {
    target: "[data-tour='first-contact']",
    title: "First Message",
    content:
      "Use the support panel to send your first command to your agent. Try something simple like 'Hello' to see it in action.",
    placement: "top",
  },
  {
    target: "[data-tour='credits-section']",
    title: "Analytics & Credits",
    content:
      "Monitor your credit usage in real-time. Credits are consumed when your agents process messages. The rolling 5-hour window shows your current rate.",
    placement: "top",
  },
  {
    target: "body",
    title: "You're Ready!",
    content:
      "You're all set! Start with a pre-built template or build your agent from scratch. Need help? Click 'Restart Tour' anytime in the help menu.",
    placement: "center",
    disableBeacon: true,
  },
];

const STORAGE_KEY = "maxemployee.onboarding_completed";

function JoyrideTooltip({
  step,
  index,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  isLast,
  isFirst,
}) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const placement = isMobile ? "bottom" : step.placement;

  return (
    <div
      className="fixed z-[9999] transition-all duration-300 ease-out"
      style={{
        ...(placement === "center"
          ? {
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }
          : placement === "bottom"
          ? { top: "50%", left: "50%", transform: "translate(-50%, 0)" }
          : { top: "30%", left: "50%", transform: "translate(-50%, 0)" }),
        width: isMobile ? "calc(100vw - 2rem)" : "420px",
        maxWidth: "420px",
      }}
    >
      <div className="relative bg-zinc-950 border border-zinc-700 shadow-2xl shadow-black/80 rounded-sm">
        {/* Header accent bar */}
        <div className="h-1 bg-gradient-to-r from-amber-500 to-amber-400" />

        {/* Close button */}
        <button
          onClick={onSkip}
          className="absolute top-3 right-3 p-1 text-zinc-500 hover:text-white transition-colors z-10"
          aria-label={t('onboarding_tour.skip_tour')}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6">
          {/* Step counter */}
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
            Step {index + 1} of {totalSteps}
          </div>

          {/* Title */}
          <h3 className="font-display text-xl uppercase text-white mb-3 pr-6">
            {step.title}
          </h3>

          {/* Content */}
          <p className="text-zinc-400 text-sm leading-relaxed">
            {step.content}
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-5 mb-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1 transition-all duration-300 ${
                  i === index
                    ? "w-6 bg-amber-500"
                    : i < index
                    ? "w-3 bg-zinc-600"
                    : "w-3 bg-zinc-800"
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={onSkip}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors px-2 py-1"
            >{t('onboarding_tour.skip_tour')}</button>

            <div className="flex items-center gap-2">
              {!isFirst && (
                <button
                  onClick={onPrev}
                  className="flex items-center gap-1 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Prev
                </button>
              )}
              <button
                onClick={onNext}
                className="flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-[10px] transition-colors"
              >
                {isLast ? (
                  <>
                    <Sparkles className="w-3.5 h-3.5" /> Finish
                  </>
                ) : (
                  <>
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Arrow for non-center placements */}
        {placement !== "center" && (
          <div
            className={`absolute left-1/2 -translate-x-1/2 ${
              placement === "bottom" ? "-top-2" : "-bottom-2"
            }`}
          >
            <div
              className={`w-4 h-4 bg-zinc-950 border-zinc-700 rotate-45 ${
                placement === "bottom"
                  ? "border-l border-t -translate-y-2"
                  : "border-r border-b translate-y-2"
              }`}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function OnboardingTour({ onComplete }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightStyle, setHighlightStyle] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const overlayRef = useRef(null);

  // Check if tour should start
  useEffect(() => {
    try {
      const completed = localStorage.getItem(STORAGE_KEY);
      if (completed !== "true") {
        // Small delay for dashboard to render
        const timer = setTimeout(() => setIsOpen(true), 500);
        return () => clearTimeout(timer);
      }
    } catch {
      // localStorage unavailable — don't show tour
    }
  }, []);

  // Check mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Position highlight over target element
  const updateHighlight = useCallback(() => {
    const step = TOUR_STEPS[currentStep];
    if (!step || step.placement === "center") {
      setHighlightStyle(null);
      return;
    }

    const target =
      step.target === "body"
        ? document.body
        : document.querySelector(step.target);

    if (!target) {
      setHighlightStyle(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    const padding = 8;

    setHighlightStyle({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });
  }, [currentStep]);

  useEffect(() => {
    if (isOpen) {
      updateHighlight();
      window.addEventListener("resize", updateHighlight);
      window.addEventListener("scroll", updateHighlight);
      return () => {
        window.removeEventListener("resize", updateHighlight);
        window.removeEventListener("scroll", updateHighlight);
      };
    }
  }, [isOpen, updateHighlight]);

  const closeTour = useCallback(() => {
    setIsOpen(false);
    setHighlightStyle(null);
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // ignore
    }
    onComplete?.();
  }, [onComplete]);

  const goNext = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      closeTour();
    }
  }, [currentStep, closeTour]);

  const goPrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const restartTour = useCallback(() => {
    setCurrentStep(0);
    setIsOpen(true);
  }, []);

  // Expose restart globally for the Header button
  useEffect(() => {
    window.__restartOnboardingTour = restartTour;
    return () => {
      delete window.__restartOnboardingTour;
    };
  }, [restartTour]);

  if (!isOpen) return null;

  const step = TOUR_STEPS[currentStep];

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[9998] bg-black/70 transition-opacity duration-300"
        onClick={closeTour}
        style={{ pointerEvents: "auto" }}
      />

      {/* Highlight cutout */}
      {highlightStyle && (
        <div
          className="fixed z-[9998] pointer-events-none transition-all duration-300"
          style={{
            ...highlightStyle,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.7)",
            border: "2px solid rgba(245, 158, 11, 0.5)",
            borderRadius: "4px",
          }}
        />
      )}

      {/* Tooltip */}
      <JoyrideTooltip
        step={step}
        index={currentStep}
        totalSteps={TOUR_STEPS.length}
        onNext={goNext}
        onPrev={goPrev}
        onSkip={closeTour}
        isFirst={currentStep === 0}
        isLast={currentStep === TOUR_STEPS.length - 1}
      />
    </>
  );
}

// Utility to restart tour from outside
export function restartOnboardingTour() {
  if (window.__restartOnboardingTour) {
    window.__restartOnboardingTour();
  }
}

// Utility to check if tour was completed
export function isOnboardingCompleted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

// Utility to reset tour state (for testing)
export function resetOnboardingTour() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
